import {
    ApiContentSection,
    ApiFullLandingPageLink,
    ApiSectionType,
    HIGHLIGHTED_PRODUCT_HASH,
    toApiFullLandingPageLink,
} from '@storefronts/common-types'
import { useContext, useEffect, useMemo, useState } from 'react'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { useCombinedProductHistoryAndLandingPageLinks } from '../../../utils/useCombinedProductHistory'
import { getDisplayData } from '../../../utils/productDisplay'
import { AiCustomerReviewSummary } from '../reviews/AiCustomerReviewSummary'
import { SectionContainer } from './Shared'
import { ScrollingLayout } from '../../../style/scrollingLayout'

type AiReviewSummarySectionProps = Extract<
    ApiContentSection,
    { type: ApiSectionType.AI_REVIEWS_SUMMARY }
>

export const AiReviewSummarySection: React.FC<{
    section: AiReviewSummarySectionProps
}> = ({ section }) => {
    const { fetchAndCacheLinksForHashes, getCachedOrderedLinksForHashes } =
        useContext(LandingPageLinksContext)

    const highlightsMainProductSlot =
        section.linkHash === HIGHLIGHTED_PRODUCT_HASH

    const cachedOrderedLinksForHash = useMemo(() => {
        if (highlightsMainProductSlot) return []
        return getCachedOrderedLinksForHashes([section.linkHash]).orderedLinks
    }, [
        getCachedOrderedLinksForHashes,
        highlightsMainProductSlot,
        section.linkHash,
    ])

    const combinedOrderedPageLinks =
        useCombinedProductHistoryAndLandingPageLinks()

    /** Without deriving this synchronously from payload/cache, SSR shows an empty placeholder until hydration. */
    const lpLinkFromLandingData = toApiFullLandingPageLink(
        highlightsMainProductSlot
            ? (combinedOrderedPageLinks[0] ?? null)
            : (cachedOrderedLinksForHash[0] ??
                  combinedOrderedPageLinks.find(
                      (l) => l.linkHash === section.linkHash,
                  ) ??
                  null),
    )

    const [
        landingPageLinkFromFetchedBatch,
        setLandingPageLinkFromFetchedBatch,
    ] = useState<ApiFullLandingPageLink | null>(null)

    useEffect(() => {
        if (lpLinkFromLandingData || highlightsMainProductSlot) return

        let isCancelled = false
        fetchAndCacheLinksForHashes([section.linkHash])
            .then((links) => {
                if (!isCancelled)
                    setLandingPageLinkFromFetchedBatch(
                        toApiFullLandingPageLink(links[0] ?? null),
                    )
            })
            .catch(() => {
                if (!isCancelled) setLandingPageLinkFromFetchedBatch(null)
            })
        return () => {
            isCancelled = true
        }
    }, [
        fetchAndCacheLinksForHashes,
        highlightsMainProductSlot,
        lpLinkFromLandingData,
        section.linkHash,
    ])

    const lpLink = lpLinkFromLandingData ?? landingPageLinkFromFetchedBatch

    const analysis = lpLink
        ? (getDisplayData(lpLink, section.variantShopifyProductId ?? null)
              .reviewAnalysis ?? null)
        : null

    if (!lpLink || !analysis) return null

    return (
        <SectionContainer maxContentWidth={ScrollingLayout.PROSE_MAX_WIDTH}>
            <AiCustomerReviewSummary
                key={`${lpLink.linkHash}-${section.variantShopifyProductId ?? ''}`}
                analysis={analysis}
                linkHash={lpLink.linkHash}
            />
        </SectionContainer>
    )
}
